/**
 * The Privileges page and the Privilege Reports page, through the legacy bridge.
 *
 * Both pages are main's (#227, #248, #267, #284) and read keyon's shapes; the
 * API answers in its own. Everything here is the translation, kept pure (bar
 * the one session read in `readsOwnPrivileges`) so it is tested on its own
 * (`__tests__/privilegesBridge.spec.js`) and `bridgeMap.js` stays a table.
 */

import { legacyAccountType } from '@/utils/accountTypeLabel';
import { SOURCE_GROUP, SOURCE_ROLE, SOURCE_BREAK_GLASS } from '@/utils/privilegeReportRows';

const arr = (v) => (Array.isArray(v) ? v : []);

/** How a grant from an emergency incident is named where main names groups. */
export const BREAK_GLASS_SOURCE = 'Emergency access (break-glass incident)';

/** How a grant from a role is named where main names groups: the port has roles, main does not. */
export const roleSourceName = (role) => `${(role && role.name) || (role && (role.slug || role.role)) || 'Role'} (role)`;

/**
 * The person a privileges read names: `user`, else `unique_ID`. One rule for
 * where the read goes (readsOwnPrivileges) and whom row 53 is asked about
 * (bridgeMap's query), so a payload with an empty `user` and someone else's
 * `unique_ID` cannot be routed to /me and answered with the caller's own.
 */
export function privilegeSubjectUser(p) {
  return p ? (p.user || p.unique_ID) : undefined;
}

/**
 * Whether `GetPrivilegeAssignmentsForSubject` should read the caller's own
 * privileges (`GET /me/privilege-assignments`) rather than someone else's
 * (`GET /privilege-assignments`, `privileges:admin`).
 *
 * Main's read-only "My Privileges" view (#284) names the caller as the
 * subject; the port answers that from the token, so the subject is not sent.
 * A read that names no one is the caller's own too: the self endpoint needs
 * no id, so it works before the page has worked out which id is theirs.
 *
 * Whether the caller may administer privileges does not enter into it. A
 * request that names someone else goes to row 53, and a caller without
 * `privileges:admin` is refused there: answering it from /me would hand back
 * the caller's own privileges as that person's, the wrong-person answer the
 * API's self endpoint refuses parameters to prevent.
 */
export function readsOwnPrivileges(p) {
  if (String((p && p.subject_type) || 'user') !== 'user') return false;

  const subject = privilegeSubjectUser(p);
  const named = subject === undefined || subject === null ? '' : String(subject).trim();

  if (named === '') return true;

  // Synchronous, because a bridge entry's path is: the store is required
  // here rather than imported, as bridgeMap's own session reads do, so this
  // module does not pull the store into the bridge's import graph.
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { useSessionStore } = require('@/stores/session');
  const session = useSessionStore();

  return session.userId !== null && session.userId !== undefined
    && named === String(session.userId);
}

/**
 * One API assignment row in the shape main's PrivilegesPage reads.
 *
 * The API: `{code, label, category, description, risk, granted, source,
 * user_override, granted_by_groups[{id,name}], granted_by_roles[{slug,name}]}`.
 * Main: `{id, code, name, effective_granted, user_override (grant|deny|null),
 * group_granted, inherited_from_groups[{group_id,name}]}`.
 *
 *  - `effective_granted` is the decision — deny wins, then any source — which
 *    is what the radios and the colour of each label reflect (#267).
 *  - `user_override` is the person's own override, the thing Save writes back;
 *    read from the source when an older answer lacks the field.
 *  - `inherited_from_groups` is main's "implicitly granted by the following
 *    group(s) this user is in" list. The port also grants through roles and
 *    through an organization-wide emergency incident, so those are named there
 *    too, marked as such: a grant the note did not explain would read as a
 *    personal one.
 *  - for a group subject: `group_granted` is the group's own grant, and
 *    nothing is inherited — a parent group is not a grant path.
 */
export function legacyAssignmentRow(row, subjectType = 'user') {
  const r = row && typeof row === 'object' ? row : {};
  const granted = Boolean(r.granted);
  const code = r.code;

  if (subjectType === 'group') {
    return {
      ...r,
      id: code,
      name: r.label,
      effective_granted: granted,
      group_granted: granted,
      user_override: null,
      inherited_from_groups: [],
    };
  }

  const groups = arr(r.granted_by_groups);
  const roles = arr(r.granted_by_roles);
  const override = r.user_override === 'grant' || r.user_override === 'deny'
    ? r.user_override
    : ({ user_deny: 'deny', user_allow: 'grant' }[r.source] || null);

  return {
    ...r,
    id: code,
    name: r.label,
    effective_granted: granted,
    user_override: override,
    group_granted: groups.length > 0 || roles.length > 0,
    // `kind` says which of the three each entry is, so the page's note can
    // say "group(s) this user is in" only when that is all it lists.
    inherited_from_groups: [
      ...groups.map((g) => ({ group_id: g.id, name: g.name, kind: SOURCE_GROUP })),
      ...roles.map((role) => ({ group_id: `role:${role.slug}`, name: roleSourceName(role), kind: SOURCE_ROLE })),
      ...(r.source === 'break_glass'
        ? [{ group_id: 'break-glass', name: BREAK_GLASS_SOURCE, kind: SOURCE_BREAK_GLASS }]
        : []),
    ],
  };
}

/** The legacy envelope's rows for a list of API rows. */
export function legacyAssignmentRows(items, subjectType = 'user') {
  return arr(items).map((row) => legacyAssignmentRow(row, subjectType));
}

/**
 * Granting groups, then roles, then an incident — the names the workbook's
 * Source column lists. Each carries its `kind`, which the workbook's Method
 * and Status read (privilegeReportRows `grantKinds`): main grants only through
 * groups, so without it a role's or an incident's grant would read "Inherited
 * from Group".
 */
function grantingSources(row) {
  return [
    ...arr(row.granting_groups).map((g) => ({ groupId: g.group_id, name: g.name, kind: SOURCE_GROUP })),
    ...arr(row.granting_roles).map((role) => ({ groupId: null, name: roleSourceName(role), kind: SOURCE_ROLE })),
    ...(row.break_glass ? [{ groupId: null, name: BREAK_GLASS_SOURCE, kind: SOURCE_BREAK_GLASS }] : []),
  ];
}

function effectiveRow(row) {
  const r = row || {};

  return {
    uniqueId: r.user_public_id,
    // The port addresses a permission by its code; main's numeric id is not
    // exposed (it collided across environments), so the code stands in for it.
    privilegeId: r.permission,
    privilegeCode: r.permission,
    effectiveGranted: Boolean(r.effective_granted),
    override: r.override || 'none',
    grantingGroups: grantingSources(r),
    risk: r.risk === undefined ? null : r.risk,
    source: r.source,
  };
}

/**
 * `GET /privilege-report/export` (snake_case) -> the resolver payload main's
 * Privilege Reports page hands to `buildReportSheets` / `buildWorkbookSpec`
 * (camelCase), so those run unchanged.
 *
 * Patients stay in `patientPrivileges` with their id and username and nothing
 * else; staff account types read in main's words ('Registered Nurse').
 */
export function privilegeReportFromExport(report) {
  const r = report && typeof report === 'object' ? report : {};
  const org = r.organization || null;
  const risk = r.risk_classification || {};
  const patients = r.patient_privileges || {};
  const exclusion = r.patient_exclusion || null;

  return {
    reportTitle: r.report_title,
    generatedAt: r.generated_at,
    organization: org && {
      accountNumber: org.account_number,
      organizationName: org.organization_name,
      orgType: org.organization_type,
    },
    users: arr(r.users).map((u) => ({
      uniqueId: u.public_id,
      username: u.username,
      accountType: legacyAccountType(u.account_type),
      firstName: u.first_name,
      lastName: u.last_name,
      displayName: u.display_name,
      email: u.email,
    })),
    groups: arr(r.groups).map((g) => ({
      groupId: g.group_id,
      name: g.name,
      parentGroupId: g.parent_group_id === undefined ? null : g.parent_group_id,
      ownerUniqueId: g.owner === undefined ? null : g.owner,
    })),
    memberships: arr(r.memberships).map((m) => ({
      groupId: m.group_id,
      uniqueId: m.user_public_id,
      assignedAt: m.assigned_at === undefined ? null : m.assigned_at,
    })),
    privileges: arr(r.permissions).map((p) => ({
      id: p.code,
      code: p.code,
      label: p.label,
      category: p.category,
      description: p.description,
      risk: p.risk === undefined ? null : p.risk,
    })),
    groupPrivileges: arr(r.group_permissions).map((gp) => ({
      groupId: gp.group_id,
      privilegeId: gp.permission,
      privilegeCode: gp.permission,
      privilegeLabel: gp.permission_label,
    })),
    userPrivileges: arr(r.user_overrides).map((o) => ({
      uniqueId: o.user_public_id,
      privilegeId: o.permission,
      privilegeCode: o.permission,
      mode: o.mode,
    })),
    effective: arr(r.effective).map(effectiveRow),
    riskClassification: {
      scored: Boolean(risk.scored),
      scoresProvisional: Boolean(risk.scores_provisional),
      riskPrivilegedUsers: arr(risk.risk_privileged_users).map((u) => ({
        uniqueId: u.user_public_id,
        label: u.label,
        displayName: u.display_name,
        riskScoreTotal: u.risk_score_total,
        highRiskCount: u.high_risk_count,
      })),
      riskPrivilegedGroups: arr(risk.risk_privileged_groups).map((g) => ({
        groupId: g.group_id,
        label: g.label,
        name: g.name,
        riskScoreTotal: g.risk_score_total,
        highRiskCount: g.high_risk_count,
      })),
    },
    patientPrivileges: {
      // Only ever false when the API said so: the workbook's red "baseline
      // could not be resolved" warning keys on `=== false`.
      baselineResolved: patients.baseline_resolved === undefined ? undefined : Boolean(patients.baseline_resolved),
      patientsPrivilegedBeyondBaseline: patients.patients_privileged_beyond_baseline,
      rows: arr(patients.rows).map((row) => ({
        ...effectiveRow(row),
        username: row.username,
        beyondBaseline: Boolean(row.beyond_baseline),
      })),
    },
    patientExclusion: exclusion && {
      policy: exclusion.policy,
      note: exclusion.note,
      omittedAccountTypeNotSet: exclusion.omitted_account_type_not_set,
      patientBaselineGroupIds: arr(exclusion.patient_baseline_group_ids),
      patientBaselineRoles: arr(exclusion.patient_baseline_roles),
    },
  };
}
